import React from 'react';
import { UseQueryResult } from 'react-query';
import {
  SearchTaskFilter,
  SearchTaskStats,
  TaskTypeGroupListExternalTaskTypeGroups,
} from '@askporter/client-grieg-lyric';
import { FilterSection, ExpandableFilterSection, type ExpandableFilterSectionItem } from '@askporter/component-library';
import { toggleValueInList } from '@askporter/utils';
import { Actions } from '../../types';

interface TaskTypesFilterProps {
  filter: SearchTaskFilter['taskType'];
  setFilters: Actions['setFilters'];
  taskTypesQuery: UseQueryResult<TaskTypeGroupListExternalTaskTypeGroups[], unknown>;
  stats: SearchTaskStats;
  t: (key: string, options?: Record<string, string | number>) => string;
}

/**
 * Renders the Task Types filter section using ExpandableFilterSection for each task type group
 * @param filter - task type filter values
 * @param setFilters - set filter reducer action
 * @param taskTypesQuery - the query object for task types, providing access to data, loading & error
 * @param stats - search task stats
 * @param t - translations function
 */
export const TaskTypesFilter: React.FC<React.PropsWithChildren<TaskTypesFilterProps>> = ({
  filter,
  setFilters,
  taskTypesQuery,
  stats,
  t,
}: TaskTypesFilterProps) => {
  const statUids = stats?.taskTypes?.map((taskType) => taskType.uid) || [];

  const handleItemChange = (taskTypeUid: string) => {
    setFilters({ taskType: toggleValueInList(taskTypeUid, filter) });
  };

  const handleSectionChange = (taskTypeUids: string[], selectAll: boolean) => {
    let updatedSelectedTaskTypes = [...filter];

    if (selectAll) {
      // Add all UIDs that aren't already selected
      taskTypeUids.forEach((uid) => {
        if (!updatedSelectedTaskTypes.includes(uid)) {
          updatedSelectedTaskTypes.push(uid);
        }
      });
    } else {
      // Remove all UIDs from selection
      updatedSelectedTaskTypes = updatedSelectedTaskTypes.filter((uid) => !taskTypeUids.includes(uid));
    }

    setFilters({ taskType: updatedSelectedTaskTypes });
  };

  // Transform task type groups into ExpandableFilterSection format
  const taskTypeGroups = taskTypesQuery?.data
    ?.map((group) => {
      const filteredTaskTypes = group.taskTypes.filter((type) => statUids?.includes(type.uid));
      if (filteredTaskTypes.length === 0) return null;

      const items: ExpandableFilterSectionItem[] = filteredTaskTypes.map((type) => ({
        uid: type.uid,
        displayName: type.displayName,
        count: stats?.taskTypes?.find((stat) => stat.uid === type.uid)?.count || 0,
      }));

      const totalCount = items.reduce((sum, item) => sum + (item.count || 0), 0);

      return {
        uid: group.uid,
        title: group.displayName,
        items,
        totalCount,
      };
    })
    .filter(Boolean);

  const hasNoFilters = statUids?.length === 0 || !taskTypeGroups?.length;

  return (
    <FilterSection
      key={`filter_section_taskType`}
      t={t}
      isLoading={taskTypesQuery?.isLoading}
      isError={taskTypesQuery?.isError}
      filterSectionTitle={t(`ns.task_filter:section:taskType`)}
      filterKey={'taskType'}
      isExpandedOnMount={!!filter?.length}
      itemsCount={filter?.length}
    >
      {hasNoFilters
        ? null // returning null here ensures the no filters message is displayed if there are no filters
        : taskTypeGroups?.map((group) => (
            <ExpandableFilterSection
              key={group.uid}
              uid={group.uid}
              title={group.title}
              items={group.items}
              selectedUids={filter}
              onItemChange={handleItemChange}
              onSectionChange={handleSectionChange}
              totalCount={group.totalCount}
              testIdPrefix="task-type-filter"
            />
          ))}
    </FilterSection>
  );
};
